import { useMemo } from "react";
import type {
  CalendarMode,
  Category,
  EventId,
  PlanEvent,
  RecurrenceRule,
  Task,
} from "../../../../shared/types";
import type { HolidayEntry } from "../../../../shared/hooks/use-holidays";
import { MobileDayView } from "./MobileDayView";
import { MobileDayStrip } from "./MobileDayStrip";
import { MonthGrid } from "../MonthGrid";
import { AddEventForm } from "../../../events/components/event-form/AddEventForm";

interface MobileCalendarContentProps {
  readonly selectedDate: string;
  readonly currentDate: string;
  readonly monthDays: string[];
  readonly displayDays: string[];
  readonly calendarMode: CalendarMode;
  readonly today: string;
  readonly now: Date;
  readonly selectedEvents: PlanEvent[];
  readonly selectedTasks: Task[];
  readonly showAddForm: boolean;
  readonly formStartTime: string | null;
  readonly formEndTime: string | null;
  readonly activeCategory: Category;
  readonly eventsByDate: ReadonlyMap<string, PlanEvent[]>;
  readonly tasksByDate: ReadonlyMap<string, Task[]>;
  readonly categoryDotsForDate: ReadonlyMap<string, Set<Category>>;
  readonly onHandleEventClick: (event: PlanEvent, anchorRect?: DOMRect) => void;
  readonly onHandleMobileSlotClick?: (start: string, end: string) => void;
  readonly onGoToNext: () => void;
  readonly onGoToPrev: () => void;
  readonly onAddEvent: (
    title: string,
    category: Category,
    date: string,
    startTime: string,
    endTime: string,
  ) => void;
  readonly onAddEventFull?: (data: {
    title: string;
    category: Category;
    date: string;
    startTime: string;
    endTime: string;
    description: string;
    notes: string;
    tags: readonly string[];
    recurrence: RecurrenceRule | null;
    detachedFromId?: EventId;
    reminderMinutes: number | null;
    reminderTime: string | null;
    reminderType: "BEFORE" | "AT_TIME" | null;
  }) => void;
  readonly onDeleteEvent?: (id: EventId) => void;
  readonly onUpdateEvent?: (
    id: EventId,
    updates: Partial<
      Pick<
        PlanEvent,
        | "title"
        | "date"
        | "startTime"
        | "endTime"
        | "notes"
        | "description"
        | "tags"
        | "recurrence"
        | "reminderMinutes"
        | "category"
      >
    >,
  ) => void;
  readonly onSetShowAddForm: (v: boolean) => void;
  readonly onSetFormStartTime: (v: string | null) => void;
  readonly onSetFormEndTime: (v: string | null) => void;
  readonly onCloseAddForm: () => void;
  readonly onTaskClick?: (task: Task) => void;
  readonly onDayClick?: (date: string) => void;
  readonly onAddTask?: () => void;
  readonly holidays?: ReadonlyMap<string, HolidayEntry>;
}

export function MobileCalendarContent(props: MobileCalendarContentProps) {
  const {
    selectedDate,
    currentDate,
    monthDays,
    displayDays,
    calendarMode,
    today,
    now,
    selectedEvents,
    selectedTasks,
    showAddForm,
    formStartTime,
    formEndTime,
    activeCategory,
    eventsByDate,
    tasksByDate,
    categoryDotsForDate,
    onHandleEventClick,
    onHandleMobileSlotClick,
    onGoToNext,
    onGoToPrev,
    onAddEvent,
    onAddEventFull,
    onDeleteEvent,
    onCloseAddForm,
    onTaskClick,
    onDayClick,
    onAddTask,
  } = props;

  const unlinkedTasksByDate = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const [date, tasks] of tasksByDate) {
      const unlinked = tasks.filter((t) => !t.eventId && !t.archived);
      if (unlinked.length > 0) map.set(date, unlinked);
    }
    return map;
  }, [tasksByDate]);

  // Mode semaine: strip de jours + vue jour en dessous
  if (calendarMode === "week") {
    return (
      <div className="flex flex-col gap-2 h-full">
        <div className="shrink-0 px-1">
          <MobileDayStrip
            days={displayDays}
            today={today}
            selectedDate={selectedDate}
            categoryDotsForDate={categoryDotsForDate}
            eventsByDate={eventsByDate}
            tasksByDate={unlinkedTasksByDate}
            calendarMode="week"
            onDayClick={(day) => onDayClick?.(day)}
            holidays={props.holidays}
          />
        </div>
        <div className="flex-1 min-h-0 overflow-hidden">
          <MobileDayView
            date={selectedDate}
            today={today}
            now={now}
            events={selectedEvents}
            tasks={selectedTasks}
            onEventClick={onHandleEventClick}
            onEventLongPress={onHandleEventClick}
            onDeleteEvent={onDeleteEvent}
            onSlotClick={onHandleMobileSlotClick}
            onSwipeLeft={onGoToNext}
            onSwipeRight={onGoToPrev}
            onTaskClick={onTaskClick}
            onAddTask={onAddTask}
          />
        </div>
        {showAddForm && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/40"
              onClick={onCloseAddForm}
            />
            <div className="fixed inset-0 z-50 bg-surface overflow-y-auto scrollbar-custom flex flex-col pb-[env(safe-area-inset-bottom)]">
              <AddEventForm
                defaultCategory={activeCategory}
                defaultDate={selectedDate}
                defaultStartTime={formStartTime ?? undefined}
                defaultEndTime={formEndTime ?? undefined}
                existingEvents={selectedEvents}
                onAdd={onAddEvent}
                onAddEvent={onAddEventFull}
                onClose={onCloseAddForm}
              />
            </div>
          </>
        )}
      </div>
    );
  }

  // Mode mois: afficher la grille du mois
  if (calendarMode === "month") {
    return (
      <>
        <MonthGrid
          days={monthDays}
          currentDate={currentDate}
          today={today}
          selectedDate={selectedDate ?? null}
          eventsByDate={eventsByDate}
          tasksByDate={unlinkedTasksByDate}
          onDayClick={(day) => onDayClick?.(day)}
        />
        {showAddForm && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/40"
              onClick={onCloseAddForm}
            />
            <div className="fixed inset-0 z-50 bg-surface overflow-y-auto scrollbar-custom flex flex-col pb-[env(safe-area-inset-bottom)]">
              <AddEventForm
                defaultCategory={activeCategory}
                defaultDate={selectedDate}
                defaultStartTime={formStartTime ?? undefined}
                defaultEndTime={formEndTime ?? undefined}
                existingEvents={selectedEvents}
                onAdd={onAddEvent}
                onAddEvent={onAddEventFull}
                onClose={onCloseAddForm}
              />
            </div>
          </>
        )}
      </>
    );
  }

  // Mode jour: strip de jours (navigation rapide) + vue jour
  return (
    <div className="flex flex-col gap-2 h-full">
      <div className="shrink-0 px-1">
        <MobileDayStrip
          days={displayDays}
          today={today}
          selectedDate={selectedDate}
          categoryDotsForDate={categoryDotsForDate}
          eventsByDate={eventsByDate}
          tasksByDate={unlinkedTasksByDate}
          calendarMode="week"
          onDayClick={(day) => onDayClick?.(day)}
          holidays={props.holidays}
          onPrev={onGoToPrev}
          onNext={onGoToNext}
        />
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        <MobileDayView
          date={selectedDate}
          today={today}
          now={now}
          events={selectedEvents}
          tasks={selectedTasks}
          onEventClick={onHandleEventClick}
          onEventLongPress={onHandleEventClick}
          onDeleteEvent={onDeleteEvent}
          onSlotClick={onHandleMobileSlotClick}
          onSwipeLeft={onGoToNext}
          onSwipeRight={onGoToPrev}
          onTaskClick={onTaskClick}
          onAddTask={onAddTask}
        />
      </div>
      {showAddForm && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40"
            onClick={onCloseAddForm}
          />
          <div className="fixed inset-0 z-50 bg-surface overflow-y-auto scrollbar-custom flex flex-col pb-[env(safe-area-inset-bottom)]">
            <AddEventForm
              defaultCategory={activeCategory}
              defaultDate={selectedDate}
              defaultStartTime={formStartTime ?? undefined}
              defaultEndTime={formEndTime ?? undefined}
              existingEvents={selectedEvents}
              onAdd={onAddEvent}
              onAddEvent={onAddEventFull}
              onClose={onCloseAddForm}
            />
          </div>
        </>
      )}
    </div>
  );
}
